import { queryOptions, skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { isNotNil, sort } from 'rambda';
import { z } from 'zod';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import { generateListResponseScheme, type ListRespondedWith } from '@/models/response';
import { isDeletedById } from '@/utils/check-messages-status';
import { fetcher } from '@/utils/fetcher';
import { avatarUrlSchema, stringifiedDateSchema } from '@/utils/schemas';
import { generateSessionKey } from '@/utils/session';

interface Options {
  type: ConversationType;
  id: number;
}

async function getSearchMessagesResult(
  keyword?: string,
  options?: Options,
  signal?: AbortSignal,
): Promise<SearchMessageResult[]> {
  const params = { Keyword: keyword };
  if (options?.type) {
    Object.assign(params, {
      [options.type === 'group' ? 'GroupID' : 'ChatSessionID']: options.id,
    });
  }

  const response = await fetcher<ListRespondedWith<SearchMessageResult>>('/GetChatSearchResult', params, {
    signal,
    schema: generateListResponseScheme(searchMessageResultSchema),
    validResponseCodes: [0, 1, 20],
  });

  if (!response?.Data) return [];

  return response.Data.map((item) => {
    const filteredDetails = sort(
      ({ Id: aId }, { Id: bId }) => bId - aId,
      item.SearchResultDetails?.filter(
        (d) =>
          !isDeletedById(
            generateSessionKey(item.ChatSessionId ? 'session' : 'group', item.ChatSessionId || item.GroupId),
            d.Id,
          ),
      ),
    );

    return filteredDetails.length > 0 ? { ...item, SearchResultDetails: filteredDetails } : null;
  })
    .filter(isNotNil)
    .toSorted(({ SearchResultDetails: aSearchResultDetails }, { SearchResultDetails: bSearchResultDetails }) =>
      aSearchResultDetails[0] && bSearchResultDetails[0]
        ? bSearchResultDetails[0].CreateTime - aSearchResultDetails[0].CreateTime
        : 0,
    );
}

const generateSearchMessagesQueryOptions = (keyword?: string, options?: Options) =>
  queryOptions({
    queryKey: ['/GetChatSearchResult', keyword, options ?? 'global'],
    queryFn: keyword ? ({ signal }) => getSearchMessagesResult(keyword, options, signal) : skipToken,
    staleTime: 10 * 1000,
    gcTime: 5 * 60 * 1000,
  });

export default function useSearchMessages(keyword = '', options?: Options) {
  const queryClient = useQueryClient();

  const query = useQuery(generateSearchMessagesQueryOptions(keyword, options));

  return {
    messages: query.data ?? [],
    clear: () => queryClient.resetQueries({ predicate: (query) => query.queryHash.includes('GetChatSearchResult') }),
    ...query,
  };
}

const searchMessageResultSchema = z.object({
  DisplayImg: avatarUrlSchema,
  SessionName: z.string(),
  ChatSessionId: z.number(),
  GroupId: z.number(),
  SearchResultDetails: z.array(
    z.object({
      Id: z.number(),
      UserId: z.number(),
      HeaderImg: avatarUrlSchema,
      Message: z.string(),
      CreateTime: stringifiedDateSchema,
      SeqId: z.number(),
      NickName: z.string(),
      MsgType: z.nativeEnum(ConversationMessageContentType),
    }),
  ),
});

export type SearchMessageResult = z.infer<typeof searchMessageResultSchema>;
